import React, { FC, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Container, Row } from 'reactstrap';

import CheckCircleIcon from 'material-react-icons/CheckCircle';

import Button from '../../components/Button/Button';
import Carousel from '../../components/Carousel/Carousel';
import FullscreenModal from '../../components/FullscreenModal/FullscreenModal';
import SanitizeHTML from '../../components/SanitizeHTML/SanitizeHTML';
import ScrollSnapSlider from '../../components/ScrollSnapSlider/ScrollSnapSlider';
import Helmet from '../../components/common/Helmet';
import useBreakpoints from '../../hooks/useBreakpoints';
import useTranslate from '../../hooks/useTranslate';
import useGolfCourses from '../../services/apis/golfCourses/api';
import getGolfCourseUrl from '../../services/apis/golfCourses/getGolfCourseUrl';
import {
  TourDestinationPathname,
  tourDestinationPathnameMap,
} from '../../services/apis/tourDestinations/consts';

const allImageKeys = require.context('./', true, /^.*image-\d+\.jpg$/).keys();

interface DestinationPageProps {
  normalizedName: TourDestinationPathname;
}

const DestinationPage: FC<DestinationPageProps> = props => {
  const { normalizedName } = props;
  const { activeLanguage, translate } = useTranslate();
  const { lg: isDesktop } = useBreakpoints();

  const [carouselActiveIndex, setCarouselActiveIndex] = useState<number>(-1);

  const { data: golfCourses } = useGolfCourses();
  const destinationGolfCourse = useMemo(
    () =>
      golfCourses?.find(
        golfCourse => golfCourse?.golfClub.id === tourDestinationPathnameMap[normalizedName]
      ),
    [golfCourses, normalizedName]
  );

  const images = useMemo(
    () => allImageKeys.filter(key => key.includes(normalizedName)).map(key => require(`${key}`)),
    [normalizedName]
  );

  return (
    <>
      <Helmet
        titleTranslationKey={`destinations.tours.${normalizedName}.meta.title`}
        descriptionTranslationKey={`destinations.tours.${normalizedName}.meta.description`}
      />
      <div
        className="d-flex align-items-center gap-3 justify-content-center flex-column py-5"
        style={{
          background: `url(${require(`./images/${normalizedName}/image-1.jpg`)}) no-repeat center`,
          backgroundSize: 'cover',
          height: '70vh',
          minHeight: 'fit-content',
        }}
      >
        <div className={`flex-shrink-0 bg-white rounded-circle p-4 overflow-hidden`}>
          <img
            src={require(`./images/${normalizedName}/logo.png`)}
            alt={String(translate(`destinations.tours.${normalizedName}.name`))}
            width={isDesktop ? 150 : 100}
            height={isDesktop ? 150 : 100}
            style={{ objectFit: 'contain' }}
          />
        </div>
        <Container>
          <h1
            className={`text-center text-white text-uppercase fw-bold mb-4 ${
              isDesktop ? 'display-4' : 'h2'
            }`}
            style={{ textShadow: '0px 0px 5px #555' }}
          >
            {translate(`destinations.tours.${normalizedName}.name`)}
          </h1>
          <h2
            className={`text-center text-white text-uppercase ${isDesktop ? '' : 'h4'}`}
            style={{ textShadow: '0px 0px 10px #555' }}
          >
            {translate(`destinations.tours.${normalizedName}.title`)}
          </h2>
        </Container>
      </div>

      <Container className="mb-5">
        <p className="my-5" style={{ whiteSpace: 'pre-line' }}>
          {translate(`destinations.tours.${normalizedName}.description`)}
        </p>
        {!!images.length && (
          <>
            <ScrollSnapSlider>
              {images.map((image, index) => (
                <img
                  key={image}
                  src={image}
                  alt=""
                  width={400}
                  onClick={e => {
                    e.preventDefault();
                    setCarouselActiveIndex(index);
                  }}
                  style={{ cursor: 'zoom-in', objectFit: 'cover' }}
                />
              ))}
            </ScrollSnapSlider>
            <FullscreenModal
              visible={carouselActiveIndex >= 0}
              close={() => setCarouselActiveIndex(-1)}
            >
              <Carousel
                activeIndex={carouselActiveIndex}
                items={images.map(item => ({ src: item, alt: item }))}
              />
            </FullscreenModal>
          </>
        )}

        <section className="my-5">
          <h3 className="h1 text-uppercase fw-bold mb-4 ">
            {translate('destinations.quick-facts', {
              name: translate(`destinations.tours.${normalizedName}.name`),
            })}
          </h3>
          <Row>
            {String(
              translate(
                `destinations.tours.${normalizedName}.features`,
                {},
                { renderInnerHtml: false }
              )
            )
              .split('\n')
              .map(feature => (
                <Col
                  key={feature}
                  xs={12}
                  md={6}
                  lg={4}
                  className="mb-4 d-flex gap-3 align-items-center"
                >
                  <div>
                    <CheckCircleIcon size={28} color="#7ca324" />
                  </div>
                  <span className="h5 mb-0">
                    <SanitizeHTML>{feature}</SanitizeHTML>
                  </span>
                </Col>
              ))}
          </Row>
          {!!destinationGolfCourse && (
            <Button
              primary
              size="lg"
              tag={Link}
              to={`/${activeLanguage.code}/booking/${getGolfCourseUrl(destinationGolfCourse)}`}
              className="w-100 justify-content-center my-4"
            >
              {translate('destinations.find-tee-times-cta')}
            </Button>
          )}
        </section>

        {(['travel', 'accommodation', 'host'] as const).map(key => {
          const content = String(
            translate(
              `destinations.tours.${normalizedName}.information.${key}`,
              {},
              { renderInnerHtml: false }
            )
          );

          return (
            content && (
              <section key={key} className="my-5">
                <h3 className="h1 text-uppercase fw-bold mb-4 ">
                  {translate(`destinations.${key}`, {
                    name: translate(`destinations.tours.${normalizedName}.name`),
                  })}
                </h3>
                <SanitizeHTML>{content}</SanitizeHTML>
              </section>
            )
          );
        })}
        <section className="my-5">
          <h3 className="h1 text-uppercase fw-bold mb-4 ">{translate('destinations.district')}</h3>
          <SanitizeHTML>
            {String(
              translate(
                `destinations.tours.${normalizedName}.information.about`,
                {},
                { renderInnerHtml: false }
              )
            )}
          </SanitizeHTML>
        </section>

        <div className="text-center">
          <Button
            primary
            size="lg"
            tag={Link}
            to={`/${activeLanguage.code}/destinations`}
            className="justify-content-center my-4"
          >
            {translate('destinations.show-all-destinations-cta')}
          </Button>
        </div>
      </Container>
    </>
  );
};

export default DestinationPage;
